import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from './utils';
import PasswordField from './components/PasswordField';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const navigate = useNavigate();
  const [token] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const resetToken = searchParams.get('token');
    if (searchParams.has('token')) {
      searchParams.delete('token');
      const remainingQuery = searchParams.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${window.location.hash}`,
      );
    }
    return resetToken;
  });

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (submissionInFlight.current || message) return;
    setError('');
    if (!token) {
      setError('This reset link is missing. Request a new password reset link');
      return;
    }

    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('Password must be 8+ characters with at least one number and one special character');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    submissionInFlight.current = true;
    setSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/reset-password`, {
        token,
        password,
      });
      setMessage(response.data.message);
      setError('');
      setTimeout(() => navigate('/login'), 3000); // Redirect to login after 3s
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === 'string' ? detail : detail?.message) || 'We could not reset your password');
      setMessage('');
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Reset Password</h2>
        <form onSubmit={handleResetPassword}>
          <div className="mb-4">
            <label htmlFor="reset-password" className="block text-sm font-bold text-gray-700">New Password</label>
            <PasswordField
              id="reset-password"
              name="password"
              autoComplete="new-password"
              placeholder="8+ chars, numbers, special"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              containerClassName="mt-1"
              className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="reset-confirm-password" className="block text-sm font-bold text-gray-700">Confirm New Password</label>
            <PasswordField
              id="reset-confirm-password"
              name="confirm-password"
              autoComplete="new-password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              containerClassName="mt-1"
              className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || Boolean(message)}
            aria-busy={submitting}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-200 disabled:cursor-wait disabled:opacity-60"
          >
            {submitting ? 'Resetting password…' : 'Reset Password'}
          </button>
          {submitting && <p role="status" className="mt-2 text-sm text-blue-800">Saving your new password…</p>}
          {message && <p role="status" className="mt-2 text-sm text-green-700">{message} Taking you to sign in…</p>}
          {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}. Check the link and form, then try again.</p>}
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
